import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate, useBlocker } from 'react-router-dom'
import {
	ArrowLeft,
	Save,
	GitBranch,
	Loader2,
	AlertCircle,
} from 'lucide-react'
import { Button, Alert, Modal } from '@components/common'
import { WorkflowStepBuilder } from '@components/features/workflows'
import { useWorkflowStore } from '@store'
import { useAuth } from '@context/AuthContext'
import { ROLES, hasPermission } from '@config/roles'

import './WorkflowForm.scss'

/**
 * WorkflowCreate Page
 *
 * Page for creating a new workflow with steps.
 * Includes form validation, unsaved changes warning, and step builder.
 */
const WorkflowCreate = ({ userRole = ROLES.COMPANY_ADMIN }) => {
	const navigate = useNavigate()
	const { user } = useAuth()

	// Get company ID from authenticated user
	// FALLBACK: If user is null in AuthContext, try to get from storage directly
	let companyId = user?.company_id

	if (!companyId) {
		try {
			const userDataStr = sessionStorage.getItem('auth_user_data') || localStorage.getItem('auth_user_data')
			if (userDataStr) {
				const userData = JSON.parse(userDataStr)
				companyId = userData?.company_id
				console.warn('⚠️ WorkflowCreate: AuthContext user is null, using company_id from storage:', companyId)
			}
		} catch (error) {
			console.error('WorkflowCreate: Error parsing user data from storage:', error)
		}
	}

	// Store
	const { createWorkflow, fetchCompanyUsers, companyUsers, loading } = useWorkflowStore()

	// Form state
	const [formData, setFormData] = useState({
		name: '',
		description: '',
	})
	const [steps, setSteps] = useState([])
	const [errors, setErrors] = useState({})
	const [hasChanges, setHasChanges] = useState(false)
	const [showExitModal, setShowExitModal] = useState(false)
	const [pendingNavigation, setPendingNavigation] = useState(null)

	// Permissions
	const canManage = hasPermission(userRole, 'canManageWorkflows')

	// Fetch company users for approver selection
	useEffect(() => {
		if (companyId) {
			fetchCompanyUsers(companyId)
		}
	}, [companyId, fetchCompanyUsers])

	// Track changes
	useEffect(() => {
		const hasFormChanges = formData.name || formData.description
		const hasStepChanges = steps.length > 0
		setHasChanges(hasFormChanges || hasStepChanges)
	}, [formData, steps])

	// Navigation blocker for unsaved changes
	const blocker = useBlocker(
		({ currentLocation, nextLocation }) =>
			hasChanges && currentLocation.pathname !== nextLocation.pathname
	)

	useEffect(() => {
		if (blocker.state === 'blocked') {
			setPendingNavigation(blocker)
			setShowExitModal(true)
		}
	}, [blocker.state])

	// Handle form field changes
	const handleChange = (field, value) => {
		setFormData((prev) => ({ ...prev, [field]: value }))
		// Clear field error
		if (errors[field]) {
			setErrors((prev) => ({ ...prev, [field]: null }))
		}
	}

	// Handle steps change
	const handleStepsChange = useCallback((newSteps) => {
		setSteps(newSteps)
		// Clear steps error
		if (errors.steps) {
			setErrors((prev) => ({ ...prev, steps: null }))
		}
	}, [errors.steps])

	// Validate form
	const validateForm = () => {
		const newErrors = {}

		if (!formData.name.trim()) {
			newErrors.name = 'Workflow name is required'
		} else if (formData.name.length < 2) {
			newErrors.name = 'Workflow name must be at least 2 characters'
		} else if (formData.name.length > 255) {
			newErrors.name = 'Workflow name cannot exceed 255 characters'
		}

		if (steps.length === 0) {
			newErrors.steps = 'At least one step is required'
		} else {
			// Validate each step has a role
			const stepsWithoutRole = steps.filter((s) => !s.role)
			if (stepsWithoutRole.length > 0) {
				newErrors.steps = 'All steps must have a role selected'
			}
		}

		setErrors(newErrors)
		return Object.keys(newErrors).length === 0
	}

	// Handle form submission
	const handleSubmit = async (e) => {
		e.preventDefault()

		if (!validateForm()) return

		const workflowData = {
			company_id: companyId,
			name: formData.name.trim(),
			description: formData.description.trim() || null,
			steps: steps.map((step) => ({
				name: step.name || null,
				role: step.role,
				approver_id: step.approver_id || null,
				sla_hours: step.sla_hours || 24,
				evidence_required: step.evidence_required ? 1 : 0,
			})),
		}

		const result = await createWorkflow(workflowData)

		if (result) {
			setHasChanges(false) // Prevent exit warning
			navigate('/workflows')
		}
	}

	// Handle cancel/back
	const handleBack = () => {
		if (hasChanges) {
			setShowExitModal(true)
		} else {
			navigate('/workflows')
		}
	}

	// Handle exit modal actions
	const handleExitConfirm = () => {
		setHasChanges(false)
		setShowExitModal(false)
		if (pendingNavigation) {
			pendingNavigation.proceed()
		} else {
			navigate('/workflows')
		}
	}

	const handleExitCancel = () => {
		setShowExitModal(false)
		if (pendingNavigation) {
			pendingNavigation.reset()
		}
		setPendingNavigation(null)
	}

	if (!canManage) {
		return (
			<div className="workflow-form-page">
				<Alert variant="error" message="You don't have permission to create workflows." />
			</div>
		)
	}

	return (
		<div className="workflow-form-page">
			{/* Header */}
			<div className="workflow-form-header">
				<div className="workflow-form-header__left">
					<button className="workflow-form-header__back" onClick={handleBack}>
						<ArrowLeft size={16} />
						Back to Workflows
					</button>
					<div className="workflow-form-header__title-row">
						<div className="workflow-form-header__icon">
							<GitBranch size={28} />
						</div>
						<div className="workflow-form-header__info">
							<h1 className="workflow-form-header__title">Create New Workflow</h1>
							<p className="workflow-form-header__subtitle">
								Define the approval steps for your compliance workflow
							</p>
						</div>
					</div>
				</div>
				<div className="workflow-form-header__actions">
					{hasChanges && <span className="workflow-form-header__unsaved">Unsaved changes</span>}
					<Button variant="outline" onClick={handleBack} disabled={loading}>
						Cancel
					</Button>
					<Button variant="primary" onClick={handleSubmit} disabled={loading}>
						{loading ? (
							<>
								<Loader2 className="spin" size={16} />
								Creating...
							</>
						) : (
							<>
								<Save size={16} />
								Create Workflow
							</>
						)}
					</Button>
				</div>
			</div>

			{/* Form */}
			<form onSubmit={handleSubmit} className="workflow-form">
				{/* Basic Details Section */}
				<div className="workflow-form__section">
					<h2 className="workflow-form__section-title">Basic Details</h2>

					<div className="workflow-form__field">
						<label htmlFor="name">
							Workflow Name <span className="required">*</span>
						</label>
						<input
							type="text"
							id="name"
							value={formData.name}
							onChange={(e) => handleChange('name', e.target.value)}
							placeholder="e.g., Standard Approval Workflow"
							className={errors.name ? 'error' : ''}
							disabled={loading}
						/>
						{errors.name && <span className="field-error">{errors.name}</span>}
					</div>

					<div className="workflow-form__field">
						<label htmlFor="description">Description</label>
						<textarea
							id="description"
							value={formData.description}
							onChange={(e) => handleChange('description', e.target.value)}
							placeholder="Describe the purpose of this workflow..."
							rows={3}
							disabled={loading}
						/>
					</div>
				</div>

				{/* Steps Section */}
				<div className="workflow-form__section">
					{errors.steps && (
						<Alert variant="error" message={errors.steps} className="workflow-form__alert" />
					)}

					<WorkflowStepBuilder
						steps={steps}
						users={companyUsers}
						onChange={handleStepsChange}
						disabled={loading}
					/>
				</div>
			</form>

			{/* Exit Confirmation Modal */}
			<Modal
				isOpen={showExitModal}
				onClose={handleExitCancel}
				title="Unsaved Changes"
				size="small"
				closeOnBackdrop={false}
			>
				<div className="exit-modal">
					<div className="exit-modal__icon">
						<AlertCircle size={48} />
					</div>
					<div className="exit-modal__content">
						<p>You have unsaved changes. Are you sure you want to leave?</p>
						<p>Your changes will be lost.</p>
					</div>
					<div className="exit-modal__actions">
						<Button variant="outline" onClick={handleExitCancel}>
							Stay
						</Button>
						<Button variant="danger" onClick={handleExitConfirm}>
							Leave
						</Button>
					</div>
				</div>
			</Modal>
		</div>
	)
}

export default WorkflowCreate
